import {observer} from 'mobx-react-lite'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {
  DataObjectId,
  ReportDataField,
  ReportFieldKey,
  Subtask,
  TaskData
} from 'yougile-model'
import {Sync} from '@client/sync'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {
  StoreContext,
  SubtasksStore,
  useStore
} from '@components/subtasks-old/subtasks-store'
import {
  useBoolean,
  useLoadedLazyTaskEffect,
  useMemoOne
} from '@containers/hooks'
import {DragControl} from '@containers/hooks-draggable'
import {SubtaskUtil} from '@extensions/subtask-util'
import {taskDataFromHub} from '@extensions/task-data-extractor'
import StraightIcon from './straight.svg'
import {SubTR} from './subtasks-tr'
import {TR} from './tr'

const hasSubtasks = (taskId: DataObjectId) => {
  const box = Sync.getCurrentSandbox()
  const hub = box.getHub(taskId)
  return SubtaskUtil.hasSubtasks(hub)
}

export interface PropsTR {
  taskId: DataObjectId
  allFields: ReportDataField[]
  resizeColumnIndex: number
  columnWidths: Record<string, number>
  dragCtl: DragControl<number>
  hasStraightLine: boolean[]
  itemsY: Subtask[]
  tbodyHeight: number
  topWithReserve: number
  bottomWithReserve: number
  getDefaultColumnWidth: (fieldKey: ReportFieldKey) => number
  doesAtLeastOneOfChildSubtasksHaveSubtasks?: boolean
}

export interface PropsSubtasksTR extends PropsTR {
  toggleAll: boolean
}

export const isBetween = (current: number, from: number, to: number) => {
  return current >= from && current <= to
}

export const SubtasksInReport = (props: {
  tasksToRender: TaskData[]
  allFields: ReportDataField[]
  resizeColumnIndex: number
  columnWidths: Record<string, number>
  dragCtl: DragControl<number>
  toggleAll: boolean
  tbodyHeight: number
  topWithReserve: number
  bottomWithReserve: number
  getDefaultColumnWidth: (fieldKey: ReportFieldKey) => number
}) => {
  return (
    <>
      {props.tasksToRender.map(t => {
        if (hasSubtasks(t.id)) {
          return (
            <RenderSubtasksTR
              key={t.id}
              taskId={t.id}
              allFields={props.allFields}
              resizeColumnIndex={props.resizeColumnIndex}
              columnWidths={props.columnWidths}
              dragCtl={props.dragCtl}
              hasStraightLine={[]}
              itemsY={[]}
              toggleAll={props.toggleAll}
              tbodyHeight={props.tbodyHeight}
              topWithReserve={props.topWithReserve}
              bottomWithReserve={props.bottomWithReserve}
              getDefaultColumnWidth={props.getDefaultColumnWidth}
            />
          )
        } else {
          return (
            <RenderTR
              key={t.id}
              taskId={t.id}
              allFields={props.allFields}
              resizeColumnIndex={props.resizeColumnIndex}
              columnWidths={props.columnWidths}
              dragCtl={props.dragCtl}
              hasStraightLine={[]}
              itemsY={[]}
              tbodyHeight={props.tbodyHeight}
              topWithReserve={props.topWithReserve}
              bottomWithReserve={props.bottomWithReserve}
              getDefaultColumnWidth={props.getDefaultColumnWidth}
            />
          )
        }
      })}
    </>
  )
}

export const EmptyContent = ({allFields}: {allFields: ReportDataField[]}) => (
  <>
    {allFields.map((_f, ix) => {
      return <td key={ix} style={{height: 40}}></td>
    })}
  </>
)

const RenderTR = (props: PropsTR) => {
  const ref = useRef(null)
  const [offsetTop, setOffsetTop] = useState<number>()
  useEffect(() => {
    ref.current?.offsetTop !== offsetTop && setOffsetTop(ref.current?.offsetTop)
  })

  const box = Sync.getCurrentSandbox()
  const hub = box.getHub(props.taskId)
  const taskData = taskDataFromHub(box, hub)

  useLoadedLazyTaskEffect(hub, 'subtasks-in-report')

  const isItInViewPort = isBetween(
    offsetTop,
    props.topWithReserve,
    props.bottomWithReserve
  )

  const componentMemo = useMemo(
    () => <TR offsetTop={offsetTop} taskData={taskData} {...props} />,
    [isItInViewPort, props.columnWidths, props.resizeColumnIndex, props.dragCtl]
  )

  return <tr ref={ref}>{componentMemo}</tr>
}

// eslint-disable-next-line max-lines-per-function
const RenderSubtasksTR = (props: PropsSubtasksTR) => {
  const open = useBoolean(false)
  const ref = useRef(null)
  const [offsetTop, setOffsetTop] = useState<number>()
  useEffect(() => {
    ref.current?.offsetTop !== offsetTop && setOffsetTop(ref.current?.offsetTop)
  })
  useEffect(() => {
    props.hasStraightLine.length < 1 && open.setValue(props.toggleAll)
  }, [props.toggleAll])

  const box = Sync.getCurrentSandbox()
  const hub = box.getHub(props.taskId)
  const taskData = taskDataFromHub(box, hub)

  useLoadedLazyTaskEffect(hub, 'subtasks-in-report-2')

  const store = useMemoOne(
    () => new SubtasksStore(props.taskId),
    [props.taskId]
  )
  const toggle = () => {
    if (!open.value) {
      Tracking.send(TrackingEvent.subtasksOpenList)
    }
    open.toggle()
  }

  const isItInViewPort = isBetween(
    offsetTop,
    props.topWithReserve,
    props.bottomWithReserve
  )

  const componentMemo = useMemo(
    () => (
      <SubTR
        offsetTop={offsetTop}
        taskData={taskData}
        toggle={toggle}
        open={open}
        {...props}
      />
    ),
    [
      isItInViewPort,
      open.value,
      props.columnWidths,
      props.resizeColumnIndex,
      props.dragCtl
    ]
  )

  return (
    <StoreContext.Provider value={store}>
      <tr ref={ref}>{componentMemo}</tr>
      {open.value && (
        <SubtaskList
          allFields={props.allFields}
          resizeColumnIndex={props.resizeColumnIndex}
          columnWidths={props.columnWidths}
          dragCtl={props.dragCtl}
          hasStraightLine={props.hasStraightLine}
          toggleAll={props.toggleAll}
          tbodyHeight={props.tbodyHeight}
          topWithReserve={props.topWithReserve}
          bottomWithReserve={props.bottomWithReserve}
          getDefaultColumnWidth={props.getDefaultColumnWidth}
        />
      )}
    </StoreContext.Provider>
  )
}

export const checkOnStraight = (ix: number, length: number) => {
  return ix + 1 !== length
}

const SubtaskList = observer(
  (props: {
    allFields: ReportDataField[]
    resizeColumnIndex: number
    columnWidths: Record<string, number>
    dragCtl: DragControl<number>
    hasStraightLine: boolean[]
    toggleAll: boolean
    tbodyHeight: number
    topWithReserve: number
    bottomWithReserve: number
    getDefaultColumnWidth: (fieldKey: ReportFieldKey) => number
  }) => {
    const [, store] = useStore()
    const doesAtLeastOneOfChildSubtasksHaveSubtasks = store.subtasks
      .map(t => hasSubtasks(t.id))
      .includes(true)

    return (
      <>
        {store.subtasks.map((t, ix, {length}) => {
          if (hasSubtasks(t.id)) {
            return (
              <RenderSubtasksTR
                key={t.id}
                taskId={t.id}
                allFields={props.allFields}
                resizeColumnIndex={props.resizeColumnIndex}
                columnWidths={props.columnWidths}
                dragCtl={props.dragCtl}
                hasStraightLine={[
                  ...props.hasStraightLine,
                  checkOnStraight(ix, length)
                ]}
                itemsY={store.subtasks}
                toggleAll={props.toggleAll}
                tbodyHeight={props.tbodyHeight}
                topWithReserve={props.topWithReserve}
                bottomWithReserve={props.bottomWithReserve}
                getDefaultColumnWidth={props.getDefaultColumnWidth}
              />
            )
          } else {
            return (
              <RenderTR
                key={t.id}
                taskId={t.id}
                allFields={props.allFields}
                resizeColumnIndex={props.resizeColumnIndex}
                columnWidths={props.columnWidths}
                dragCtl={props.dragCtl}
                hasStraightLine={[...props.hasStraightLine, false]}
                itemsY={store.subtasks}
                tbodyHeight={props.tbodyHeight}
                topWithReserve={props.topWithReserve}
                bottomWithReserve={props.bottomWithReserve}
                getDefaultColumnWidth={props.getDefaultColumnWidth}
                doesAtLeastOneOfChildSubtasksHaveSubtasks={
                  doesAtLeastOneOfChildSubtasksHaveSubtasks
                }
              />
            )
          }
        })}
      </>
    )
  }
)

export const getStraightLinesIcons = (count: number) => {
  return new Array(count).fill(null).map((_el, ix) => (
    <div className="-mt-6" key={ix}>
      <StraightIcon />
    </div>
  ))
}
